/**
 * Firestore cloud sync for SeeTex documents
 *
 * Documents are stored under:
 *   users/{uid}/documents/{docId}
 *
 * This module is used by both web (web/services/file-sync.ts) and
 * Tauri (src/services/cloud-sync-tauri.ts) — it only depends on the
 * Firebase JS SDK and a FirebaseApp instance.
 */

import {
    getFirestore,
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    deleteDoc,
    onSnapshot,
    Timestamp,
    type Firestore,
    type Unsubscribe,
} from "firebase/firestore";
import type { FirebaseApp } from "firebase/app";
import type { StoredDocument } from "../../src/triggers/types";

export interface CloudSync {
    saveDocument(doc: StoredDocument): Promise<void>;
    loadDocuments(): Promise<StoredDocument[]>;
    loadDocument(docId: string): Promise<StoredDocument | null>;
    deleteDocument(docId: string): Promise<void>;
    /**
     * Subscribe to real-time updates from the cloud.
     * Returns an unsubscribe function.
     */
    watchDocuments(callback: (docs: StoredDocument[]) => void): Unsubscribe;
    unsubscribe(): void;
}

function toStoredDocument(id: string, data: Record<string, unknown>): StoredDocument {
    return {
        id,
        name: (data.name as string) ?? "Untitled",
        content: (data.content as string) ?? "",
        mathZones: (data.mathZones as StoredDocument["mathZones"]) ?? [],
        lastModified:
            data.lastModified instanceof Timestamp
                ? data.lastModified.toMillis()
                : typeof data.lastModified === "number"
                    ? data.lastModified
                    : Date.now(),
        imageFiles: (data.imageFiles as string[]) ?? undefined,
    };
}

/**
 * Create a CloudSync instance bound to a specific user.
 * Call this when the user signs in; call unsubscribe() when they sign out.
 */
export function createCloudSync(app: FirebaseApp, uid: string): CloudSync {
    const db: Firestore = getFirestore(app);
    const userDocsRef = () => collection(db, "users", uid, "documents");
    const userDocRef = (docId: string) => doc(db, "users", uid, "documents", docId);

    let activeUnsubscribe: Unsubscribe | null = null;

    return {
        async saveDocument(storedDoc: StoredDocument): Promise<void> {
            const payload: Record<string, unknown> = {
                name: storedDoc.name,
                content: storedDoc.content,
                mathZones: storedDoc.mathZones,
                lastModified: Timestamp.fromMillis(storedDoc.lastModified),
            };
            if (storedDoc.imageFiles && storedDoc.imageFiles.length > 0) {
                payload.imageFiles = storedDoc.imageFiles;
            }
            await setDoc(userDocRef(storedDoc.id), payload);
        },

        async loadDocuments(): Promise<StoredDocument[]> {
            const snap = await getDocs(userDocsRef());
            return snap.docs.map((d) =>
                toStoredDocument(d.id, d.data() as Record<string, unknown>)
            );
        },

        async loadDocument(docId: string): Promise<StoredDocument | null> {
            const snap = await getDoc(userDocRef(docId));
            if (!snap.exists()) return null;
            return toStoredDocument(snap.id, snap.data() as Record<string, unknown>);
        },

        async deleteDocument(docId: string): Promise<void> {
            await deleteDoc(userDocRef(docId));
        },

        watchDocuments(callback: (docs: StoredDocument[]) => void): Unsubscribe {
            if (activeUnsubscribe) activeUnsubscribe();
            activeUnsubscribe = onSnapshot(userDocsRef(), (snap) => {
                const docs = snap.docs.map((d) =>
                    toStoredDocument(d.id, d.data() as Record<string, unknown>)
                );
                callback(docs);
            });
            return activeUnsubscribe;
        },

        unsubscribe(): void {
            if (activeUnsubscribe) {
                activeUnsubscribe();
                activeUnsubscribe = null;
            }
        },
    };
}
